import React, { useState, useEffect, useCallback, useRef } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { B } from './constants';
import { destinations as destinationsAPI, subscriptions } from './api';
import { trackScreen, trackActivity, startActivityTracking, stopActivityTracking } from './lib/activityEvents';
import { identifyUser, resetUser } from './lib/analytics';

import { useTranslation } from 'react-i18next';
import './i18n';
import { AuthProvider, useAuth } from './AuthContext';
import { ToastProvider, useToast } from './ToastContext';
import { CurrencyProvider } from './CurrencyContext';
import Navbar          from './Navbar';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import HomeScreen    from './screens/HomeScreen';
import DetailScreen  from './screens/DetailScreen';
import SavedScreen   from './SavedScreen';
import AIScreen      from './screens/AIScreen';
import AIAccordionScreen from './screens/AIAccordionScreen';
import MockReelsScreen from './screens/MockReelsScreen';
import MockBlogsScreen from './screens/MockBlogsScreen';
import MockProfileScreen from './screens/MockProfileScreen';
import ReelsScreen   from './screens/ReelsScreen';
import ReelsFeed     from './components/ReelsFeed';
import PromoUploadModal from './components/PromoUploadModal';
import AuthScreen    from './AuthScreen';
import UsernamePrompt from './components/UsernamePrompt';
import TripPlannerScreen from './screens/TripPlannerScreen';
import ManualPlannerScreen from './screens/ManualPlannerScreen';
import PlannerChooserScreen from './screens/PlannerChooserScreen';
import TripDetailScreen from './screens/TripDetailScreen';
import BlogListScreen from './screens/BlogListScreen';
import BlogViewScreen from './screens/BlogViewScreen';
import MyTripsScreen from './screens/MyTripsScreen';
import SettingsScreen from './SettingsScreen';
import UserJourneyScreen from './screens/UserJourneyScreen';
import AIChatScreen from './screens/AIChatScreen';
import DiscoverScreen from './screens/DiscoverScreen';
import PublicBlogScreen from './screens/PublicBlogScreen';
import BlogEditorScreen from './screens/BlogEditorScreen';
import TermsScreen from './screens/TermsScreen';
import PrivacyScreen from './screens/PrivacyScreen';
import AboutScreen from './screens/AboutScreen';
import ForgotPasswordScreen from './screens/ForgotPasswordScreen';
import ResetPasswordScreen from './screens/ResetPasswordScreen';
import VerifyEmailScreen from './screens/VerifyEmailScreen';
import CreatorProfileScreen from './screens/CreatorProfileScreen';
import SubscriptionScreen from './screens/SubscriptionScreen';
import TripDetailRoute from './components/TripDetailRoute';
import TripRoute from './components/TripRoute';
import RecapReelsScreen from './screens/RecapReelsScreen';
import GuideScreen from './screens/GuideScreen';
import FlightSearchScreen from './screens/FlightSearchScreen';
import HotelSearchScreen from './screens/HotelSearchScreen';
import GlobalSearchScreen from './screens/GlobalSearchScreen';
import GuidesListScreen from './screens/GuidesListScreen';
import AcceptInviteScreen from './screens/AcceptInviteScreen';
import { loadPendingInvite } from './lib/pendingInvite';
import CreatorAnalyticsScreen from './screens/CreatorAnalyticsScreen';
import AdminActivityScreen from './screens/AdminActivityScreen';
import PromoScreen from './screens/PromoScreen';
import AIChatWidget from './components/AIChatWidget';
import CookieConsent from './components/CookieConsent';
import './App.css';


const globalStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,700;1,700;1,900&family=Montserrat:wght@300;400;600;700;900&display=swap');
  * { box-sizing:border-box; margin:0; padding:0; }
  html { overflow-x: hidden; }
  body, #root {
    max-width: 100vw;
    overflow-x: clip;
  }
  body { font-family:'Montserrat',sans-serif; background:${B.sandPattern}; color:${B.night}; }
  .responsive-shell {
    width: 100%;
    min-width: 320px;
    overflow-x: clip;
  }
  @keyframes fadeUp  { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:translateY(0)} }
  @keyframes shimmer { 0%{background-position:0%} 100%{background-position:200%} }
  @keyframes spin    { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
  ::-webkit-scrollbar{width:6px}
  ::-webkit-scrollbar-track{background:#f0ede8}
  ::-webkit-scrollbar-thumb{background:${B.forest};border-radius:4px}
  input,button{outline:none}
  input:focus-visible,button:focus-visible{outline:2px solid ${B.forest};outline-offset:2px}
  @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
  @media (max-width: 768px) {
    .split-pane { flex-direction: column !important; height: calc(100vh - 70px) !important; overflow: hidden !important; }
    .split-pane-left { width: 100% !important; max-height: none !important; flex: 1 1 0% !important; overflow-y: auto !important; min-height: 0 !important; }
    .split-pane-right { width: 100% !important; height: 300px !important; min-height: 300px !important; flex-shrink: 0 !important; }
    .leaflet-container { touch-action: pan-x pan-y !important; }
  }
  .hero-search:hover { box-shadow: 0 12px 40px rgba(0,0,0,0.35) !important; }
  .hero-search-input:focus { outline: none !important; }
  .hero-search:focus-within { box-shadow: 0 0 0 3px ${B.forest}40, 0 8px 32px rgba(0,0,0,0.25) !important; }
  @media (max-width: 480px) {
    .split-pane { flex-direction: column !important; }
    .split-pane-left { width: 100% !important; }
    .split-pane-right { width: 100% !important; height: 250px !important; }
  }
  .glass {
    background: rgba(255,255,255,0.08);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    border: 1px solid rgba(255,255,255,0.12);
  }
  .glass-dark {
    background: rgba(15,25,35,0.78);
    backdrop-filter: blur(16px);
    -webkit-backdrop-filter: blur(16px);
    border: 1px solid rgba(255,255,255,0.08);
  }
  .glass-card {
    background: rgba(255,255,255,0.85);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    border: 1px solid rgba(255,255,255,0.3);
  }
  .gradient-overlay {
    background: linear-gradient(160deg, rgba(15,25,35,0.75) 0%, rgba(15,25,35,0.4) 55%, rgba(26,107,74,0.3) 100%);
  }
  .gradient-overlay-animated {
    background: linear-gradient(160deg, rgba(15,25,35,0.75) 0%, rgba(15,25,35,0.4) 55%, rgba(26,107,74,0.3) 100%);
    background-size: 200% 200%;
    animation: gradientShift 8s ease infinite;
  }
  @keyframes gradientShift {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
  @keyframes fadeIn { from{opacity:0} to{opacity:1} }
  @keyframes slideUp { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:translateY(0)} }
  @keyframes scaleIn { from{opacity:0;transform:scale(0.95)} to{opacity:1;transform:scale(1)} }
  @keyframes shake { 0%,100%{transform:translateX(0)} 25%{transform:translateX(-4px)} 75%{transform:translateX(4px)} }
  @media (prefers-reduced-motion: reduce) {
    *, *::before, *::after { animation-duration: 0.01ms !important; transition-duration: 0.01ms !important; }
  }
  .focus-ring:focus-visible { outline: 3px solid ${B.forest}; outline-offset: 2px; border-radius: 4px; }
  .skip-to-content { position:absolute; left:-9999px; z-index:9999; padding:12px 20px; background:${B.forest}; color:white; font-family:'Montserrat',sans-serif; font-size:14px; font-weight:700; text-decoration:none; border-radius:0 0 8px 0; }
  .skip-to-content:focus { left:0; top:0; }
 `;

function Spinner({ message = 'Loading...' }) {
  return (
    <div style={{ minHeight:'60vh', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:16 }}>
      <div style={{ width:44, height:44, borderRadius:'50%', border:`3px solid ${B.sand}`, borderTopColor:B.forest, animation:'spin 0.8s linear infinite' }} />
      <p style={{ color:B.muted, fontFamily:"'Montserrat',sans-serif", fontSize:14 }}>{message}</p>
    </div>
  );
}

// In-app screens that have their own URL. Screens that need in-memory data
// (detail, tripDetail, blogView, …) keep the current URL and live in history state.
const SCREEN_PATHS = {
  home: '/',
  ai: '/ai-planner',
  planner: '/planner',
  manualPlanner: '/manual-planner',
  reels: '/reels',
  blogs: '/blogs',
  trips: '/mytrips',
  saved: '/saved',
  profile: '/profile',
  settings: '/settings',
  chat: '/chat',
  discover: '/discover',
  userJourney: '/journey',
  hotelSearch: '/hotels',
  globalSearch: '/search',
};
const PATH_ALIASES = { '/home': 'home', '/trips': 'trips', '/my-trips': 'trips', '/ai': 'ai', '/blog': 'blogs' };

export function screenFromPath(pathname) {
  const p = (pathname || '/').toLowerCase().replace(/\/+$/, '') || '/';
  return Object.keys(SCREEN_PATHS).find(k => SCREEN_PATHS[k] === p) || PATH_ALIASES[p] || null;
}

function InnerApp() {
  const { user, loading: authLoading, updateUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [screen,       setScreen]       = useState(() => screenFromPath(window.location.pathname) || 'home');
  const isInitialMount = useRef(true);
  const initialScreenPending = useRef(true);

  useEffect(() => {
    if (initialScreenPending.current) {
      const t = setTimeout(() => {
        initialScreenPending.current = false;
        trackScreen(screen);
      }, 0);
      return () => clearTimeout(t);
    }
    trackScreen(screen);
  }, [screen]);

  useEffect(() => {
    if (authLoading) return;
    if (user?.id) {
      identifyUser(user.id, { email: user.email, name: user.name });
    } else {
      resetUser();
    }
  }, [user, authLoading]);

  useEffect(() => {
    startActivityTracking();
    return () => stopActivityTracking();
  }, []);

  // Skip transition on initial mount
  useEffect(() => {
    isInitialMount.current = false;
  }, []);

  const [selectedDest, setSelectedDest] = useState(null);
  const [tripData,     setTripData]     = useState(null);
  const [blogData,     setBlogData]     = useState(null);
  const [manualPlanData, setManualPlanData] = useState(null);
  const [plannerPrefill, setPlannerPrefill] = useState(null);
  const [vibe,         setVibe]         = useState('all');
  const [saved,        setSaved]        = useState([]);
  const [allDests,     setAllDests]     = useState([]);
  const [destsLoading, setDestsLoading] = useState(true);
  const [destsError,   setDestsError]   = useState(null);
  const [storyViewer,  setStoryViewer]  = useState(null);
  const [showUpload,   setShowUpload]   = useState(false);
  const [tripJourneyData, setTripJourneyData] = useState(null);
  const [aiPrefill, setAiPrefill] = useState(null);
  const [aiPrefillDays, setAiPrefillDays] = useState(null);
  const [aiEditTripId, setAiEditTripId] = useState(null);

  // Legacy /?screen=<name> deep links (e.g. back from the standalone /trips/:id page).
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const target = q.get('screen');
    if (!target || !SCREEN_PATHS[target]) return;
    const dest = (q.get('destination') || '').trim();
    if (target === 'ai' && dest) setAiPrefill(dest);
    setScreen(target);
    q.delete('screen'); q.delete('destination');
    const rest = q.toString();
    navigate(SCREEN_PATHS[target] + (rest ? `?${rest}` : ''), { replace: true });
  }, [navigate]);

  // An invite opened while signed out is accepted as soon as the user signs in.
  useEffect(() => {
    if (authLoading || !user) return;
    const token = loadPendingInvite();
    if (token) navigate(`/trips/invite/${encodeURIComponent(token)}`, { replace: true });
  }, [user, authLoading, navigate]);

  // "Plan this trip" deep links (/?plan=Rome&days=3) from reels, blogs and promo pages.
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const dest = (q.get('plan') || '').trim();
    if (!dest) return;
    const days = parseInt(q.get('days'), 10);
    setAiPrefill(dest);
    setAiPrefillDays(days > 0 && days <= 30 ? days : null);
    setScreen('ai');
    q.delete('plan'); q.delete('days');
    const rest = q.toString();
    navigate(SCREEN_PATHS.ai + (rest ? `?${rest}` : ''), { replace: true });
  }, [navigate]);
  const [aiKey, setAiKey] = useState(0);

  const saveRequestRef = useRef({ id: 0, destId: null });
  const savingRef = useRef(false);

  const showToast = useToast();

  const fetchDestinations = useCallback(async () => {
    setDestsLoading(true);
    setDestsError(null);
    try {
      const data = await destinationsAPI.list();
      const dests = data.destinations || data;
      setAllDests(Array.isArray(dests) ? dests : []);
    } catch (err) {
      console.warn('Could not load destinations:', err.message);
      setAllDests([]);
    } finally {
      setDestsLoading(false);
    }
  }, []);

  const fetchSaved = useCallback(async () => {
    if (!user) { setSaved([]); return; }
    try {
      const { saved: list } = await import('./api').then(m => m.saved.list());
      setSaved(list || []);
    } catch { setSaved([]); }
  }, [user]);

  useEffect(() => { fetchDestinations(); }, [fetchDestinations]);
  useEffect(() => { fetchSaved(); }, [fetchSaved]);

  // Sign-in opens over the current screen instead of replacing it, so the user lands
  // back exactly where they were (with any in-progress planner state) after logging in.
  const [authOpen, setAuthOpen] = useState(false);

  // Back/forward and direct URL changes drive the screen.
  const lastLocationKey = useRef(location.key);
  useEffect(() => {
    if (lastLocationKey.current === location.key) return;
    lastLocationKey.current = location.key;
    const fromPath = screenFromPath(location.pathname) || 'home';
    let next = location.state?.screen || fromPath;
    const requiredData = { detail: selectedDest, creatorProfile: selectedDest, tripDetail: tripData, blogView: blogData };
    if (next in requiredData && !requiredData[next]) next = fromPath;
    setAuthOpen(location.state?.openAuth === true);
    setScreen(next);
  }, [location.key]); // eslint-disable-line react-hooks/exhaustive-deps

  const goScreen = (s, data = null) => {
    if (s === 'auth') { setAuthOpen(true); return; }
    setAuthOpen(false);
    setScreen(s);
    const path = SCREEN_PATHS[s];
    if (path) {
      if (path !== location.pathname || location.state?.screen) navigate(path);
    } else if (s !== screen) {
      navigate(location.pathname + location.search, { state: { screen: s } });
    }
    if (s === 'detail' && data) setSelectedDest(data);
    if (s === 'tripDetail' && data) setTripData(data);
    if (s === 'blogView' && data) setBlogData(data);
    if (s === 'tripPlanner' && data) setTripJourneyData(data);
    if (s === 'ai') { setAiPrefill(data?.prefillDestination || null); setAiPrefillDays(data?.prefillDays || null); setAiEditTripId(data?.editTripId || null); setAiKey(k => k + 1); }
    if (s === 'manualPlanner') setManualPlanData(data || null);
    if (s === 'planner') setPlannerPrefill(data?.prefillDestination || data?.prefillQuery || null);
    if (s !== 'detail') setSelectedDest(null);
    if (s !== 'tripDetail') setTripData(null);
    if (s !== 'blogView') setBlogData(null);
    if (s === 'creatorProfile' && data) setSelectedDest(data); // data = username
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getScreenClass = () => {
    return '';
  };

  if (authLoading) return (<><style>{globalStyles}</style><Spinner message="Starting GreatBreak..." /></>);

  const toggleSave = async (dest) => {
    if (!user) { goScreen('auth'); return; }
    if (savingRef.current) return;
    savingRef.current = true;
    
    const isAlreadySaved = saved.some(d => d.id === dest.id);
    const currentRequestId = ++saveRequestRef.current.id;
    saveRequestRef.current.destId = dest.id;
    
    // Update local state first for immediate feedback
    setSaved(prev => isAlreadySaved 
      ? prev.filter(d => d.id !== dest.id) 
      : [...prev, dest]);
    
    // Persist to backend
    try {
      if (isAlreadySaved) {
        await destinationsAPI.unsave(dest.id);
      } else {
        await destinationsAPI.save(dest.id);
        trackActivity('content', 'item_saved', { type: 'destination', id: dest.id });
      }
      // Only update if this is still the latest request
      if (saveRequestRef.current.id !== currentRequestId) {
        // Request was superseded - revert to current saved state
        if (isAlreadySaved) {
          setSaved(prev => [...prev, dest]);
        } else {
          setSaved(prev => prev.filter(d => d.id !== dest.id));
        }
        return;
      }
    } catch (err) {
      // Only revert if this is still the latest request
      if (saveRequestRef.current.id === currentRequestId) {
        console.warn('Failed to save:', err.message);
        setSaved(prev => isAlreadySaved 
          ? [...prev, dest] 
          : prev.filter(d => d.id !== dest.id));
      }
    } finally {
      savingRef.current = false;
    }
  };

  return (
    <><style>{globalStyles}</style>
    <div className="responsive-shell" style={{ minHeight: '100vh' }}>
    <a href="#main-content" className="skip-to-content">Skip to content</a>
    <div id="main-content" className="main-content">
      <Navbar screen={screen} savedCount={saved.length} goScreen={goScreen} onAuthRequired={() => goScreen('auth')} />
      <PWAInstallPrompt />

      {destsError && (
        <div style={{ background: '#f8d7da', color: '#721c24', padding: 12, textAlign: 'center' }}>
          ⚠️ {destsError}
        </div>
      )}

       {screen === 'home' && (
        <div className={getScreenClass()}>
          <HomeScreen 
            destinations={allDests} 
            saved={saved} 
            onToggleSave={toggleSave} 
            goScreen={goScreen} 
            user={user} 
            onAuthRequired={() => goScreen('auth')}
            onOpenStory={setStoryViewer}
            onOpenUpload={() => user ? setShowUpload(true) : goScreen('auth')}
            vibe={vibe} setVibe={setVibe}
            loading={destsLoading}
          />
        </div>
      )}
      {screen === 'detail'  && selectedDest  && <div className={getScreenClass()}><DetailScreen key={selectedDest.id} dest={selectedDest} saved={saved} onToggleSave={toggleSave} goScreen={goScreen} /></div>}
      {screen === 'saved'   && <div className={getScreenClass()}><SavedScreen  destinations={allDests} saved={saved} onToggleSave={toggleSave} goScreen={goScreen} user={user} onAuthRequired={() => goScreen('auth')} /></div>}
      {screen === 'planner' && <div className={getScreenClass()}><PlannerChooserScreen goScreen={goScreen} prefillDestination={plannerPrefill} /></div>}
      {screen === 'ai'      && <div className={getScreenClass()} key={aiKey}><AIAccordionScreen goScreen={goScreen} user={user} onAuthRequired={() => goScreen('auth')} prefillDestination={aiPrefill} prefillDays={aiPrefillDays} editTripId={aiEditTripId} /></div>}
      {screen === 'manualPlanner' && (
        <div className={getScreenClass()}><ManualPlannerScreen goScreen={goScreen} user={user} showToast={showToast} onAuthRequired={() => goScreen('auth')} initialCity={manualPlanData} /></div>
      )}
      {screen === 'tripPlanner' && (
        <div className={getScreenClass()}><TripPlannerScreen goScreen={goScreen} showToast={showToast} initialData={tripJourneyData} /></div>
      )}
      {screen === 'tripDetail' && tripData && <div className={getScreenClass()}><TripDetailScreen trip={tripData} goScreen={goScreen} showToast={showToast} user={user} /></div>}
      {screen === 'blogs' && <div className={getScreenClass()}><MockBlogsScreen goScreen={goScreen} user={user} onAuthRequired={() => goScreen('auth')} /></div>}
      {screen === 'blogView' && blogData && <div className={getScreenClass()}><BlogViewScreen blog={blogData} goScreen={goScreen} showToast={showToast} /></div>}

       {screen === 'trips' && <div className={getScreenClass()}><MyTripsScreen goScreen={goScreen} user={user} onAuthRequired={() => goScreen('auth')} showToast={showToast} /></div>}

        {screen === 'profile' && <div className={getScreenClass()}><MockProfileScreen goScreen={goScreen} user={user} onAuthRequired={() => goScreen('auth')} /></div>}
       {screen === 'settings' && user && <div className={getScreenClass()}><SettingsScreen goScreen={goScreen} /></div>}
       {screen === 'reels'   && <div className={getScreenClass()}><MockReelsScreen goScreen={goScreen} user={user} onAuthRequired={() => goScreen('auth')} /></div>}
       {screen === 'chat'    && <div className={getScreenClass()}><AIChatScreen goScreen={goScreen} user={user} onAuthRequired={() => goScreen('auth')} /></div>}
       {screen === 'discover' && <div className={getScreenClass()}><DiscoverScreen goScreen={goScreen} /></div>}
         {screen === 'userJourney' && <div className={getScreenClass()}><UserJourneyScreen goScreen={goScreen} user={user} onAuthRequired={() => goScreen('auth')} setTripJourneyData={setTripJourneyData} /></div>}
          {screen === 'subscription' && <div className={getScreenClass()}><SubscriptionScreen goScreen={goScreen} user={user} /></div>}
          {screen === 'creatorProfile' && <div className={getScreenClass()}><CreatorProfileScreen goScreen={goScreen} user={user} /></div>}
           {screen === 'flightSearch' && <div className={getScreenClass()}><FlightSearchScreen goScreen={goScreen} showToast={showToast} /></div>}
           {screen === 'hotelSearch' && <div className={getScreenClass()}><HotelSearchScreen goScreen={goScreen} showToast={showToast} user={user} onAuthRequired={() => goScreen('auth')} /></div>}
            {screen === 'globalSearch' && <GlobalSearchScreen goScreen={goScreen} />}
       </div>
     </div>
    {['home', 'discover'].includes(screen) && (
      <AIChatWidget user={user} onAuthRequired={() => goScreen('auth')} goScreen={goScreen} />
    )}
    {storyViewer && <ReelsFeed
      promos={storyViewer.promos}
      startIndex={storyViewer.startIndex}
      onClose={() => setStoryViewer(null)}
      onDeleted={(id) => setStoryViewer(prev => prev ? { ...prev, promos: prev.promos.filter(p => p.id !== id) } : null)}
      user={user}
      onAuthRequired={() => { setStoryViewer(null); goScreen('auth'); }}
      onPlanTrip={(p) => { setStoryViewer(null); goScreen('ai', { prefillDestination: p.destination }); }}
    />}
    {showUpload  && <PromoUploadModal user={user} onClose={() => setShowUpload(false)} onSuccess={() => { setShowUpload(false); showToast('🎉 Promo published!'); }} />}
    {user && user.usernameConfirmed === false && !authOpen && (
      <UsernamePrompt user={user} onSaved={(patch) => { updateUser(patch); showToast(`You're @${patch.username} now 🎉`); }} />
    )}
    {authOpen && (
      <div role="dialog" aria-modal="true" aria-label="Sign in" style={{ position: 'fixed', inset: 0, zIndex: 10000, overflowY: 'auto', background: B.night }}>
        <AuthScreen
          onSuccess={() => {
            setAuthOpen(false);
            if (tripJourneyData && screen === 'userJourney') goScreen('tripPlanner');
            showToast('Welcome to GreatBreak! 🎉');
          }}
          goHome={() => setAuthOpen(false)}
        />
        <button type="button" onClick={() => setAuthOpen(false)} aria-label="Close sign in"
          style={{ position: 'fixed', top: 14, right: 14, zIndex: 10001, width: 40, height: 40, borderRadius: '50%', border: '1px solid rgba(255,255,255,0.25)', background: 'rgba(15,25,35,0.6)', color: '#fff', fontSize: 18, cursor: 'pointer' }}>
          ✕
        </button>
      </div>
    )}
    <CookieConsent />
    </>
  );
}

// Provides the active currency/locale, wired to the signed-in user's saved
// preference and the current i18n language. Must sit inside AuthProvider.
function CurrencyBridge({ children }) {
  const { user } = useAuth();
  const { i18n } = useTranslation();
  return (
    <CurrencyProvider language={i18n.language} user={user}>
      {children}
    </CurrencyProvider>
  );
}

// Stripe redirects here after checkout. Emit the completion event once, then
// send the user home (the subscription screen is not a destination page).
function SubscriptionSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const navigate = useNavigate();
  const firedRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const key = sessionId || 'none';
    const emit = (tier) => {
      if (cancelled || firedRef.current === key) return;
      firedRef.current = key;
      const isPaid = typeof tier === 'string' && tier.trim() !== '' && tier.trim() !== 'FREE';
      if (isPaid) trackActivity('subscription', 'subscription_completed', { plan: tier });
    };
    subscriptions.getCurrent()
      .then(current => emit(current?.user?.subscriptionTier ?? null))
      .catch(() => emit(null))
      .finally(() => { if (!cancelled) navigate('/', { replace: true }); });
    return () => { cancelled = true; };
  }, [sessionId, navigate]);

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ color: B.muted, fontSize: 14 }}>Finishing up…</div>
    </div>
  );
}

export default function App() {
  // Hide splash screen after React mounts (here, not in InnerApp, so standalone routes like /promo/:id are uncovered too)
  useEffect(() => {
    const splash = document.getElementById('splash');
    if (splash) {
      requestAnimationFrame(() => {
        splash.classList.add('hidden');
      });
    }
  }, []);
  // Outer App - just routes, timeout handled in InnerApp
  return (
    <BrowserRouter>
      <AuthProvider>
        <CurrencyBridge>
        <ToastProvider>
        <Routes>
          <Route path="/terms" element={<TermsScreen goScreen={() => window.history.back()} />} />
          <Route path="/privacy" element={<PrivacyScreen goScreen={() => window.history.back()} />} />
          <Route path="/about" element={<AboutScreen goScreen={() => window.history.back()} />} />
          <Route path="/forgot-password" element={<ForgotPasswordScreen />} />
          <Route path="/reset-password/:token" element={<ResetPasswordScreen />} />
          <Route path="/verify/:token" element={<VerifyEmailScreen />} />
          <Route path="/blog/public/:tripId" element={<PublicBlogScreen />} />
          <Route path="/blog/edit/:tripId" element={<BlogEditorScreen />} />
          <Route path="/c/:username" element={<CreatorProfileScreen />} />
          <Route path="/subscription" element={<SubscriptionScreen />} />
          <Route path="/subscriptions/success" element={<SubscriptionSuccess />} />
          <Route path="/promo/:id" element={<PromoScreen />} />
          <Route path="/trip/:shareSlug" element={<TripDetailRoute />} />
          <Route path="/trips/:id" element={<TripRoute />} />
          <Route path="/trips/invite/:token" element={<AcceptInviteScreen />} />
          <Route path="/guide/:slug" element={<GuideScreen />} />
          <Route path="/guides" element={<GuidesListScreen />} />
          <Route path="/creator/analytics" element={<CreatorAnalyticsScreen />} />
          <Route path="/admin" element={<AdminActivityScreen />} />
          <Route path="/flights/search" element={<FlightSearchScreen goScreen={() => window.history.back()} />} />
          <Route path="/*" element={<InnerApp />} />
        </Routes>
        </ToastProvider>
        </CurrencyBridge>
      </AuthProvider>
    </BrowserRouter>
  );
}
